import React, { useState } from 'react'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import {
  Cell,
  Expander,
  ExpanderContent,
  Grid,
  HeaderBlock,
  InputRow,
  TextField,
  Button,
  useAtSize,
  ZionForm,
  Separator,
  colors,
} from '@fs/zion-ui'
import { ArrowCaret } from '@fs/zion-icon'
import { boolAttr } from '@fs/zion-frontend-friends'
import { i18n } from '@fs/zion-locale'
import { SearchParams } from '../../types/data-types'
import sendAnalytics from '../../services/analytics-helper'
import EventFields from './EventFields'

const inputContainerCss = css`
  margin: auto;
  max-width: 900px;
  text-align: left;
  width: 100%;
`

const rowCss = css`
  display: flex;

  & > div {
    width: 100%;
  }

  & > span:first-of-type span {
    width: 100%;
  }

  & > button[emphasis='high'] {
    margin-top: 28px;
    min-width: 100px;
  }

  & > span button {
    margin-top: 9px;
    margin-right: 20px;
  }

  &[is-mobile] {
    flex-direction: column;

    & > div > div {
      margin-bottom: 0;
    }

    & > button {
      margin: 12px 0 24px 0;
      width: calc(100% - 12px);
    }

    & div[name='self.surname.value'] {
      margin-top: -28px;
    }
  }

  & button {
    margin-top: -15px;
  }

  & [is-open] button {
    margin-top: 0;
  }
`

const expandedInputsCss = css`
  margin-right: 12px;
`

const inputRowCss = css`
  display: block;
  & > div:first-of-type {
    margin-right: 24px;
  }

  & > div:last-of-type {
    margin-right: 0;
  }
`

const nameFieldSwapLocalesRegEx = /^ja|^km|^ko|^mn|^ro|^to|^vi|^zh/

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type FormikErrors = SearchParams

export default function Search({
  header,
  params,
  onSubmit,
  showMore,
  analytics,
}: {
  header?: string | undefined
  params?: Record<string, unknown>
  onSubmit?: (values: SearchParams) => void
  showMore?: boolean
  analytics?: Record<string, string>
}): JSX.Element {
  const [t] = useTranslation()
  const atSize = useAtSize()
  const isMobile = atSize({ md: false, default: true })
  const swapNames = nameFieldSwapLocalesRegEx.test(i18n.language)
  const [open, setOpen] = useState(false)

  const validate = (values: SearchParams): FormikErrors => {
    if (!values.self?.surname?.value) {
      return {
        self: {
          surname: {
            value: t('ancestors.fields.required', 'Required'),
          },
        },
      }
    }
    return {}
  }

  return (
    <div css={inputContainerCss}>
      <ZionForm initialValues={params} validate={validate}>
        {(zionForm) => {
          const { values } = zionForm
          function handleSubmit(): void {
            if (onSubmit) {
              onSubmit(values)
            }
            sendAnalytics({ ...analytics, link_name: 'Search' })
          }

          function handleKeyPress(event): void {
            if (event.key === 'Enter') {
              handleSubmit()
            }
          }

          const inputs = (
            <InputRow css={inputRowCss}>
              <Grid columns={12}>
                <Cell columns={isMobile ? 12 : 6}>
                  {swapNames ? (
                    <TextField
                      data-testid="search-surname"
                      label={t('ancestors.surname.label', 'Last or Maiden Name (Required)')}
                      name="self.surname.value"
                      onKeyPress={(event) => {
                        handleKeyPress(event)
                      }}
                    />
                  ) : (
                    <TextField
                      data-testid="search-given-name"
                      label={t('ancestors.given-name.label', 'First and Middle Name')}
                      name="self.given.value"
                      onKeyPress={(event) => {
                        handleKeyPress(event)
                      }}
                    />
                  )}
                </Cell>
                <Cell columns={isMobile ? 12 : 6}>
                  {swapNames ? (
                    <TextField
                      data-testid="search-given-name"
                      label={t('ancestors.given-name.label', 'First and Middle Name')}
                      name="self.given.value"
                      onKeyPress={(event) => {
                        handleKeyPress(event)
                      }}
                    />
                  ) : (
                    <TextField
                      data-testid="search-surname"
                      label={t('ancestors.surname.label', 'Last or Maiden Name (Required)')}
                      name="self.surname.value"
                      onKeyPress={(event) => {
                        handleKeyPress(event)
                      }}
                    />
                  )}
                </Cell>
              </Grid>
            </InputRow>
          )

          return (
            <>
              {header && <HeaderBlock centered size="sm" heading={header} />}
              <div css={rowCss} is-mobile={boolAttr(isMobile)}>
                {showMore && (
                  <div is-open={boolAttr(open)}>
                    {inputs}
                    <Expander open={open} setOpen={setOpen}>
                      <ExpanderContent>
                        <div css={expandedInputsCss}>
                          <EventFields name="birth" type="birth" />
                          <EventFields name="death" type="death" />
                        </div>
                      </ExpanderContent>
                    </Expander>
                    <Separator size="xxs" />
                    <Button
                      data-testid="show-more-less-button"
                      onClick={(event) => {
                        setOpen(!open)
                        event.target.blur()
                        sendAnalytics({ ...analytics, link_name: open ? 'Less Search Options' : 'More Search Options' })
                      }}
                      Icon={ArrowCaret}
                      iconDirection={open ? 'up' : 'down'}
                    >
                      {open
                        ? t('ancestors.less-search-options.action', 'Less search options')
                        : t('ancestors.more-search-options.action', 'More search options')}
                    </Button>
                  </div>
                )}
                {!showMore && inputs}
                <Button
                  data-testid="search-button"
                  emphasis="high"
                  onClick={handleSubmit}
                  disabled={!values.self?.surname?.value}
                >
                  {t('common-ui:search.action', 'Search')}
                </Button>
              </div>
            </>
          )
        }}
      </ZionForm>
    </div>
  )
}
